import {
  useState,
  useMemo,
  useRef,
  useEffect,
  FC,
  CSSProperties,
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
} from 'react';

/* ─── CONSTANTS ─────────────────────────────────────────── */
const DEPARTMENTS: string[] = [
  'Computer Science',
  'Mathematics',
  'Physics',
  'Chemistry',
  'Biology',
  'Mechanical Eng',
  'Civil Eng',
  'Electronics',
];
const STATUSES: string[] = [
  'Active',
  'Inactive',
  'Graduated',
  'Suspended',
  'On Leave',
];
const YEARS: string[] = ['1st Year', '2nd Year', '3rd Year', '4th Year'];
const GRADES: string[] = ['A+', 'A', 'B+', 'B', 'C+', 'C', 'D', 'F'];
const GENDERS: string[] = ['Male', 'Female', 'Other'];

/* ─── TYPES ─────────────────────────────────────────────── */
interface StatusMeta {
  bg: string;
  text: string;
  dot: string;
}
// test line
const STATUS_META: Record<string, StatusMeta> = {
  Active: { bg: '#e6f4ea', text: '#1e6b3e', dot: '#34a853' },
  Inactive: { bg: '#f1f3f4', text: '#5f6368', dot: '#9aa0a6' },
  Graduated: { bg: '#e8f0fe', text: '#1a56db', dot: '#4285f4' },
  Suspended: { bg: '#fce8e6', text: '#b31412', dot: '#ea4335' },
  'On Leave': { bg: '#fef7e0', text: '#7a4f00', dot: '#f9ab00' },
};

interface Student {
  id: number;
  name: string;
  email: string;
  roll: string;
  department: string;
  year: string;
  status: string;
  grade: string;
  gender: string;
  gpa: string;
  phone: string;
  dob: string;
  enrolled: string;
  address: string;
  notes: string;
  attendance: number;
}

interface Column {
  key: keyof Student;
  label: string;
  w: number;
}

type DialogMode = 'add' | 'edit' | 'view' | 'delete';

interface DialogState {
  mode: DialogMode;
  data?: Partial<Student>;
  ids?: number[];
}

interface ToastItem {
  id: number;
  msg: string;
  type: 'success' | 'error' | 'info';
}

interface InlineEdit {
  id: number;
  key: keyof Student;
}

/* ─── DATA GENERATION ───────────────────────────────────── */
function rnd<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function rndInt(a: number, b: number): number {
  return Math.floor(Math.random() * (b - a + 1)) + a;
}
function fmtDate(d: Date): string {
  return d.toISOString().split('T')[0];
}
function rndDate(y1: number, y2: number): Date {
  const start = new Date(y1, 0, 1).getTime();
  const end = new Date(y2, 11, 31).getTime();
  return new Date(start + Math.random() * (end - start));
}

const FIRST: string[] = [
  'Aarav',
  'Priya',
  'Rohan',
  'Sneha',
  'Arjun',
  'Meera',
  'Karthik',
  'Divya',
  'Vikram',
  'Anjali',
  'Mohammed',
  'Aisha',
  'Chen',
  'Sofia',
  'Marcus',
  'Elena',
  'Yuki',
  'Fatima',
  'Leon',
  'Nadia',
  'Omar',
  'Diego',
  'Mei',
  'Andre',
  'Lena',
  'Sam',
  'Zara',
  'Raj',
  'Isabella',
  'Ethan',
];
const LAST: string[] = [
  'Sharma',
  'Patel',
  'Kumar',
  'Singh',
  'Nair',
  'Reddy',
  'Gupta',
  'Iyer',
  'Chen',
  'Rodriguez',
  'Kim',
  'Tanaka',
  'Müller',
  'Johnson',
  'Okafor',
  'Fernandez',
  'Nakamura',
  'Hassan',
  'Petrov',
  'Andersen',
  'Nguyen',
  'Williams',
  'Santos',
  'Kowalski',
  'Dubois',
  'Ahmed',
  'Russo',
  'Park',
  'Mehta',
  'Das',
];

let _id = 1000;
function generateStudents(n: number = 40): Student[] {
  return Array.from({ length: n }, (): Student => {
    const dob = rndDate(2000, 2006);
    const enrolled = rndDate(2020, 2024);
    const fn = rnd(FIRST);
    const ln = rnd(LAST);
    return {
      id: ++_id,
      name: `${fn} ${ln}`,
      email: `${fn.toLowerCase()}.${ln.toLowerCase()}${rndInt(10, 99)}@edu.ac.in`,
      roll: `STU-${_id}`,
      department: rnd(DEPARTMENTS),
      year: rnd(YEARS),
      status: rnd(STATUSES),
      grade: rnd(GRADES),
      gender: rnd(GENDERS),
      gpa: (Math.random() * 3 + 7).toFixed(2),
      phone: `+91 ${rndInt(7000000000, 9999999999)}`,
      dob: fmtDate(dob),
      enrolled: fmtDate(enrolled),
      address: `${rndInt(1, 999)}, Main Road, Chennai`,
      notes: '',
      attendance: rndInt(60, 100),
    };
  });
}

const INIT_STUDENTS: Student[] = generateStudents(40);

const COLUMNS: Column[] = [
  { key: 'roll', label: 'Roll No', w: 110 },
  { key: 'name', label: 'Name', w: 160 },
  { key: 'department', label: 'Department', w: 150 },
  { key: 'year', label: 'Year', w: 100 },
  { key: 'grade', label: 'Grade', w: 80 },
  { key: 'gpa', label: 'GPA', w: 80 },
  { key: 'status', label: 'Status', w: 110 },
  { key: 'attendance', label: 'Attendance', w: 110 },
  { key: 'enrolled', label: 'Enrolled', w: 110 },
];

/* ─── SUB-COMPONENTS ────────────────────────────────────── */
const Badge: FC<{ status: string }> = ({ status }) => {
  const m: StatusMeta = STATUS_META[status] ?? STATUS_META['Inactive'];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: m.bg,
        color: m.text,
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 9px',
        letterSpacing: 0.3,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: m.dot,
          flexShrink: 0,
        }}
      />
      {status}
    </span>
  );
};

const AttendanceBar: FC<{ val: number }> = ({ val }) => {
  const color = val >= 85 ? '#34a853' : val >= 75 ? '#f9ab00' : '#ea4335';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div
        style={{
          flex: 1,
          height: 5,
          background: '#e8eaed',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${val}%`,
            height: '100%',
            background: color,
            borderRadius: 4,
            transition: 'width .4s',
          }}
        />
      </div>
      <span style={{ fontSize: 11, color, fontWeight: 600, minWidth: 34 }}>
        {val}%
      </span>
    </div>
  );
};

const Toast: FC<{ toasts: ToastItem[] }> = ({ toasts }) => (
  <div
    style={{
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    }}
  >
    {toasts.map((t) => (
      <div
        key={t.id}
        style={{
          background:
            t.type === 'success'
              ? '#1e6b3e'
              : t.type === 'error'
                ? '#b31412'
                : '#1a56db',
          color: '#fff',
          padding: '10px 18px',
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 500,
          boxShadow: '0 4px 16px #0003',
          animation: 'slideIn .25s ease',
        }}
      >
        {t.msg}
      </div>
    ))}
  </div>
);

/* ─── FIELD PROPS ────────────────────────────────────────── */
interface FieldProps {
  label: string;
  name: keyof Student;
  type?: 'text' | 'email' | 'tel' | 'date' | 'number' | 'select' | 'textarea';
  opts?: string[];
  def?: string | number;
  dialogData?: Partial<Student>;
  readOnly?: boolean;
  formRef: React.MutableRefObject<Partial<Student>>;
}

const Field: FC<FieldProps> = ({
  label,
  name,
  type = 'text',
  opts,
  def = '',
  dialogData,
  readOnly = false,
  formRef,
}) => {
  const inputId = `${name}-field`;
  const initVal =
    dialogData?.[name] !== undefined ? String(dialogData[name]) : String(def);
  const v =
    dialogData?.[name] !== undefined ? String(dialogData[name]) : String(def);

  const [val, setVal] = useState(v);

  useEffect(() => {
    (formRef.current as Record<string, unknown>)[name] = v;
  }, [v, name, formRef]);

  const handleChange = (v: string) => {
    setVal(v);
    (formRef.current as Record<string, unknown>)[name] = v;
  };

  const base: CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 12px',
    border: '1.5px solid #e0e3e8',
    borderRadius: 8,
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    background: '#fafbfc',
    color: '#1c1c1c',
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <label
        htmlFor={inputId}
        style={{
          display: 'block',
          fontSize: 11,
          fontWeight: 700,
          color: '#5f6368',
          marginBottom: 4,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </label>
      {type === 'select' ? (
        <select
          id={inputId}
          value={val}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            handleChange(e.target.value)
          }
          style={base}
          disabled={readOnly}
        >
          {opts?.map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea
          id={inputId}
          value={val}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            handleChange(e.target.value)
          }
          rows={3}
          style={{ ...base, resize: 'vertical' }}
          readOnly={readOnly}
        />
      ) : (
        <input
          id={inputId}
          type={type}
          value={val}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleChange(e.target.value)
          }
          style={base}
          readOnly={readOnly}
        />
      )}
    </div>
  );
};

/* ─── MAIN COMPONENT ────────────────────────────────────── */
export default function StudentManagement(): React.ReactElement {
  const [students, setStudents] = useState<Student[]>(INIT_STUDENTS);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [sortKey, setSortKey] = useState<keyof Student>('roll');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [search, setSearch] = useState<string>('');
  const [filterDept, setFilterDept] = useState<string>('All');
  const [filterStatus, setFilterStatus] = useState<string>('All');
  const [filterYear, setFilterYear] = useState<string>('All');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [visibleCols, setVisibleCols] = useState<Set<string>>(
    new Set(COLUMNS.map((c) => c.key))
  );
  const [showColMenu, setShowColMenu] = useState<boolean>(false);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [inlineEdit, setInlineEdit] = useState<InlineEdit | null>(null);
  const [inlineVal, setInlineVal] = useState<string>('');
  const [bulkAction, setBulkAction] = useState<string>('');

  const formRef = useRef<Partial<Student>>({});
  const toastIdRef = useRef<number>(0);

  /* ── TOAST ── */
  const addToast = (msg: string, type: ToastItem['type'] = 'success'): void => {
    const id = ++toastIdRef.current;
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3000);
  };

  /* ── FILTER + SORT + PAGINATE ── */
  const filtered = useMemo<Student[]>(() => {
    let d = [...students];
    if (search) {
      const q = search.toLowerCase();
      d = d.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.roll.toLowerCase().includes(q) ||
          s.email.toLowerCase().includes(q)
      );
    }
    if (filterDept !== 'All') d = d.filter((s) => s.department === filterDept);
    if (filterStatus !== 'All') d = d.filter((s) => s.status === filterStatus);
    if (filterYear !== 'All') d = d.filter((s) => s.year === filterYear);

    d.sort((a, b) => {
      let av: string | number = a[sortKey] as string | number;
      let bv: string | number = b[sortKey] as string | number;
      if (typeof av === 'string') {
        av = av.toLowerCase();
        bv = (bv as string).toLowerCase();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return d;
  }, [
    students,
    search,
    filterDept,
    filterStatus,
    filterYear,
    sortKey,
    sortDir,
  ]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const pageData = filtered.slice((page - 1) * pageSize, page * pageSize);

  /* ── SELECT ── */
  const allPageSelected =
    pageData.length > 0 && pageData.every((s) => selected.has(s.id));
  const toggleAll = (): void => {
    if (allPageSelected) {
      setSelected((p) => {
        const n = new Set(p);
        pageData.forEach((s) => n.delete(s.id));
        return n;
      });
    } else {
      setSelected((p) => {
        const n = new Set(p);
        pageData.forEach((s) => n.add(s.id));
        return n;
      });
    }
  };
  const toggleOne = (id: number): void =>
    setSelected((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  /* ── SORT ── */
  const handleSort = (key: keyof Student): void => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  /* ── CRUD ── */
  const openAdd = (): void =>
    setDialog({
      mode: 'add',
      data: {
        department: DEPARTMENTS[0],
        year: YEARS[0],
        status: 'Active',
        grade: 'A',
        gender: 'Male',
        gpa: '8.00',
        attendance: 85,
      },
    });
  const openEdit = (s: Student): void =>
    setDialog({ mode: 'edit', data: { ...s } });
  const openView = (s: Student): void => setDialog({ mode: 'view', data: s });
  const openDel = (ids: number[]): void => setDialog({ mode: 'delete', ids });

  const saveStudent = (): void => {
    const d = formRef.current as Record<string, string>;
    if (!d.name || !d.email) {
      addToast('Name & Email are required', 'error');
      return;
    }
    if (dialog?.mode === 'add') {
      const ns: Student = {
        ...(d as unknown as Student),
        id: Date.now(),
        roll: `STU-${Date.now() % 10000}`,
        enrolled: fmtDate(new Date()),
        attendance: Number(d.attendance) || 85,
      };
      setStudents((p) => [ns, ...p]);
      addToast(`Student "${ns.name}" added!`);
    } else {
      setStudents((p) =>
        p.map((s) =>
          s.id === (dialog?.data?.id as number)
            ? {
                ...s,
                ...(d as unknown as Partial<Student>),
                attendance: Number(d.attendance),
              }
            : s
        )
      );
      addToast(`Student "${d.name}" updated!`);
    }
    setDialog(null);
  };

  const deleteStudents = (ids: number[]): void => {
    setStudents((p) => p.filter((s) => !ids.includes(s.id)));
    setSelected((p) => {
      const n = new Set(p);
      ids.forEach((id) => n.delete(id));
      return n;
    });
    addToast(`${ids.length} student(s) deleted`, 'error');
    setDialog(null);
  };

  /* ── BULK ACTION ── */
  const applyBulk = (): void => {
    if (!bulkAction || selected.size === 0) return;
    if (bulkAction === 'delete') {
      openDel([...selected]);
      return;
    }
    setStudents((p) =>
      p.map((s) => (selected.has(s.id) ? { ...s, status: bulkAction } : s))
    );
    addToast(
      `Status changed to "${bulkAction}" for ${selected.size} student(s)`
    );
    setSelected(new Set());
    setBulkAction('');
  };

  /* ── INLINE EDIT ── */
  const startInline = (s: Student, key: keyof Student): void => {
    setInlineEdit({ id: s.id, key });
    setInlineVal(String(s[key]));
  };
  const saveInline = (): void => {
    if (!inlineEdit) return;
    setStudents((p) =>
      p.map((s) =>
        s.id === inlineEdit.id ? { ...s, [inlineEdit.key]: inlineVal } : s
      )
    );
    addToast('Cell updated');
    setInlineEdit(null);
  };

  /* ── EXPORT CSV ── */
  const exportCSV = (): void => {
    const header = COLUMNS.map((c) => c.label).join(',');
    const rows = filtered.map((s) =>
      COLUMNS.map((c) => `"${s[c.key]}"`).join(',')
    );
    const blob = new Blob([header + '\n' + rows.join('\n')], {
      type: 'text/csv',
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'students.csv';
    a.click();
    addToast('CSV exported!');
  };

  /* ── STATS ── */
  const stats = useMemo(
    () => ({
      total: students.length,
      active: students.filter((s) => s.status === 'Active').length,
      avgGpa: (
        students.reduce((a, s) => a + parseFloat(s.gpa), 0) / students.length
      ).toFixed(2),
      avgAtt: Math.round(
        students.reduce((a, s) => a + s.attendance, 0) / students.length
      ),
    }),
    [students]
  );

  /* ── STYLES ── */
  const S = {
    wrap: {
      fontFamily: "'DM Sans', sans-serif",
      background: '#f4f6fb',
      minHeight: '100vh',
      paddingBottom: 60,
    } as CSSProperties,
    hdr: {
      background: 'linear-gradient(135deg,#1a56db 0%,#1e3a8a 100%)',
      padding: '28px 32px 24px',
      color: '#fff',
    } as CSSProperties,
    card: {
      background: '#fff',
      borderRadius: 14,
      boxShadow: '0 1px 4px #0001,0 4px 16px #0001',
      padding: '16px 20px',
    } as CSSProperties,
    btn: {
      border: 'none',
      borderRadius: 8,
      padding: '8px 16px',
      fontWeight: 600,
      fontSize: 13,
      cursor: 'pointer',
      fontFamily: 'inherit',
    } as CSSProperties,
    th: {
      padding: '10px 14px',
      textAlign: 'left' as const,
      fontSize: 11,
      fontWeight: 700,
      color: '#5f6368',
      letterSpacing: 0.5,
      textTransform: 'uppercase' as const,
      userSelect: 'none' as const,
      cursor: 'pointer',
      whiteSpace: 'nowrap' as const,
    },
    td: {
      padding: '10px 14px',
      fontSize: 13,
      color: '#1c1c1c',
      verticalAlign: 'middle' as const,
    },
    over: {
      position: 'fixed' as const,
      inset: 0,
      background: '#0008',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    modal: {
      background: '#fff',
      borderRadius: 16,
      width: 600,
      maxWidth: '95vw',
      maxHeight: '90vh',
      overflow: 'auto' as const,
      boxShadow: '0 20px 60px #0006',
    } as CSSProperties,
  };

  const readOnly = dialog?.mode === 'view';

  return (
    <div style={S.wrap}>
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
      <style>{`
        @keyframes slideIn { from { opacity:0; transform:translateX(24px) } to { opacity:1; transform:none } }
        @keyframes fadeUp  { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:none } }
        .row-hover:hover { background:#f0f4ff !important; }
        .row-sel { background:#e8f0fe !important; }
        .sort-icon { opacity:.35; margin-left:4px; font-size:10px; }
        .sort-icon.active { opacity:1; color:#1a56db; }
        .btn-icon { background:none; border:1.5px solid #e0e3e8; border-radius:8px; padding:6px 10px; cursor:pointer; font-size:15px; color:#5f6368; transition:.15s; font-family:inherit; }
        .btn-icon:hover { background:#f0f4ff; border-color:#1a56db; color:#1a56db; }
        input[type=checkbox] { width:16px; height:16px; cursor:pointer; accent-color:#1a56db; }
        .inline-cell:hover { background:#f0f4ff; border-radius:4px; cursor:pointer; }
        ::-webkit-scrollbar { width:6px; height:6px; }
        ::-webkit-scrollbar-track { background:#f1f3f4; }
        ::-webkit-scrollbar-thumb { background:#c5cad3; border-radius:4px; }
      `}</style>

      {/* ── HEADER ── */}
      <div style={S.hdr}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.5 }}>
              🎓 Student Management System
            </div>
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 3 }}>
              Academic Year 2024–25 ·{' '}
              {new Date().toLocaleDateString('en-IN', { dateStyle: 'long' })}
            </div>
          </div>
          <button
            style={{
              ...S.btn,
              background: '#fff',
              color: '#1a56db',
              fontSize: 14,
            }}
            onClick={openAdd}
          >
            + Add Student
          </button>
        </div>

        {/* STAT CARDS */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4,1fr)',
            gap: 12,
            marginTop: 20,
          }}
        >
          {(
            [
              { label: 'Total Students', val: stats.total, icon: '👥' },
              { label: 'Active', val: stats.active, icon: '✅' },
              { label: 'Avg GPA', val: stats.avgGpa, icon: '📊' },
              { label: 'Avg Attendance', val: `${stats.avgAtt}%`, icon: '📅' },
            ] as const
          ).map((c) => (
            <div
              key={c.label}
              style={{
                background: '#ffffff18',
                borderRadius: 10,
                padding: '12px 16px',
                backdropFilter: 'blur(8px)',
              }}
            >
              <div style={{ fontSize: 20, marginBottom: 4 }}>{c.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{c.val}</div>
              <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>
                {c.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {/* ── FILTERS ── */}
        <div
          style={{
            ...S.card,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <input
            placeholder="🔍  Search by name, roll, email…"
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{
              flex: '1 1 220px',
              padding: '8px 14px',
              border: '1.5px solid #e0e3e8',
              borderRadius: 8,
              fontSize: 13,
              fontFamily: 'inherit',
              outline: 'none',
            }}
          />

          {(
            [
              [
                'Dept',
                DEPARTMENTS,
                filterDept,
                (v: string) => {
                  setFilterDept(v);
                  setPage(1);
                },
              ],
              [
                'Status',
                STATUSES,
                filterStatus,
                (v: string) => {
                  setFilterStatus(v);
                  setPage(1);
                },
              ],
              [
                'Year',
                YEARS,
                filterYear,
                (v: string) => {
                  setFilterYear(v);
                  setPage(1);
                },
              ],
            ] as [string, string[], string, (v: string) => void][]
          ).map(([lbl, opts, val, set]) => (
            <select
              key={lbl}
              value={val}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                set(e.target.value)
              }
              style={{
                padding: '8px 12px',
                border: '1.5px solid #e0e3e8',
                borderRadius: 8,
                fontSize: 13,
                fontFamily: 'inherit',
                background: '#fafbfc',
                color: '#1c1c1c',
                outline: 'none',
              }}
            >
              <option value="All">All {lbl}s</option>
              {opts.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          ))}

          <div style={{ position: 'relative' }}>
            <button
              className="btn-icon"
              onClick={() => setShowColMenu((p) => !p)}
            >
              ⚙ Columns
            </button>
            {showColMenu && (
              <div
                style={{
                  position: 'absolute',
                  top: 38,
                  right: 0,
                  background: '#fff',
                  border: '1.5px solid #e0e3e8',
                  borderRadius: 10,
                  padding: '10px 14px',
                  zIndex: 100,
                  width: 200,
                  boxShadow: '0 8px 24px #0002',
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#5f6368',
                    marginBottom: 8,
                    letterSpacing: 0.5,
                  }}
                >
                  VISIBLE COLUMNS
                </div>
                {COLUMNS.map((c) => (
                  <label
                    key={c.key}
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      padding: '4px 0',
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={visibleCols.has(c.key)}
                      onChange={() =>
                        setVisibleCols((p) => {
                          const n = new Set(p);
                          n.has(c.key) ? n.delete(c.key) : n.add(c.key);
                          return n;
                        })
                      }
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          <button className="btn-icon" onClick={exportCSV}>
            📥 Export
          </button>
        </div>

        {/* ── BULK ACTION BAR ── */}
        {selected.size > 0 && (
          <div
            style={{
              background: '#1a56db',
              borderRadius: 10,
              padding: '10px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              animation: 'fadeUp .2s ease',
            }}
          >
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>
              {selected.size} student(s) selected
            </span>
            <select
              value={bulkAction}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setBulkAction(e.target.value)
              }
              style={{
                padding: '6px 12px',
                borderRadius: 7,
                border: 'none',
                fontSize: 13,
                fontFamily: 'inherit',
                outline: 'none',
              }}
            >
              <option value="">— Bulk Action —</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  Set Status: {s}
                </option>
              ))}
              <option value="delete">🗑 Delete Selected</option>
            </select>
            <button
              style={{ ...S.btn, background: '#fff', color: '#1a56db' }}
              onClick={applyBulk}
            >
              Apply
            </button>
            <button
              style={{
                ...S.btn,
                background: 'transparent',
                color: '#fff',
                border: '1.5px solid #ffffff55',
              }}
              onClick={() => setSelected(new Set())}
            >
              Clear
            </button>
          </div>
        )}

        {/* ── GRID ── */}
        <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 13,
              }}
            >
              <thead>
                <tr
                  style={{
                    background: '#f8f9fc',
                    borderBottom: '1.5px solid #e8eaed',
                  }}
                >
                  <th style={{ ...S.th, width: 42 }}>
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleAll}
                    />
                  </th>
                  {COLUMNS.filter((c) => visibleCols.has(c.key)).map((c) => (
                    <th
                      key={c.key}
                      style={{ ...S.th, width: c.w }}
                      onClick={() => handleSort(c.key)}
                    >
                      {c.label}
                      <span
                        className={`sort-icon${sortKey === c.key ? ' active' : ''}`}
                      >
                        {sortKey === c.key
                          ? sortDir === 'asc'
                            ? '▲'
                            : '▼'
                          : '⇅'}
                      </span>
                    </th>
                  ))}
                  <th style={{ ...S.th, width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageData.map((st) => (
                  <>
                    <tr
                      key={st.id}
                      className={`row-hover${selected.has(st.id) ? ' row-sel' : ''}`}
                      style={{
                        borderBottom: '1px solid #f0f2f5',
                        transition: 'background .15s',
                      }}
                    >
                      <td style={{ ...S.td, width: 42 }}>
                        <input
                          type="checkbox"
                          checked={selected.has(st.id)}
                          onChange={() => toggleOne(st.id)}
                        />
                      </td>

                      {COLUMNS.filter((c) => visibleCols.has(c.key)).map(
                        (c) => {
                          const isInline =
                            inlineEdit?.id === st.id &&
                            inlineEdit?.key === c.key;
                          if (isInline) {
                            return (
                              <td key={c.key} style={S.td}>
                                <input
                                  autoFocus
                                  value={inlineVal}
                                  onChange={(
                                    e: ChangeEvent<HTMLInputElement>
                                  ) => setInlineVal(e.target.value)}
                                  onBlur={saveInline}
                                  onKeyDown={(
                                    e: KeyboardEvent<HTMLInputElement>
                                  ) => e.key === 'Enter' && saveInline()}
                                  style={{
                                    width: '100%',
                                    padding: '4px 8px',
                                    border: '1.5px solid #1a56db',
                                    borderRadius: 6,
                                    fontSize: 13,
                                    fontFamily: 'inherit',
                                    outline: 'none',
                                  }}
                                />
                              </td>
                            );
                          }
                          const cellVal = st[c.key];
                          return (
                            <td
                              key={c.key}
                              style={S.td}
                              className="inline-cell"
                              onDoubleClick={() => startInline(st, c.key)}
                            >
                              {c.key === 'status' ? (
                                <Badge status={String(cellVal)} />
                              ) : c.key === 'attendance' ? (
                                <AttendanceBar val={Number(cellVal)} />
                              ) : c.key === 'gpa' ? (
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color:
                                      parseFloat(String(cellVal)) >= 9
                                        ? '#1e6b3e'
                                        : parseFloat(String(cellVal)) >= 8
                                          ? '#1a56db'
                                          : '#5f6368',
                                  }}
                                >
                                  {String(cellVal)}
                                </span>
                              ) : c.key === 'grade' ? (
                                <span
                                  style={{ fontWeight: 700, color: '#1a56db' }}
                                >
                                  {String(cellVal)}
                                </span>
                              ) : (
                                String(cellVal)
                              )}
                            </td>
                          );
                        }
                      )}

                      <td style={S.td}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            className="btn-icon"
                            style={{ fontSize: 13 }}
                            title="View"
                            onClick={() => openView(st)}
                          >
                            👁
                          </button>
                          <button
                            className="btn-icon"
                            style={{ fontSize: 13 }}
                            title="Edit"
                            onClick={() => openEdit(st)}
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-icon"
                            style={{ fontSize: 13 }}
                            title="Expand"
                            onClick={() =>
                              setExpandedRow(
                                expandedRow === st.id ? null : st.id
                              )
                            }
                          >
                            {expandedRow === st.id ? '▲' : '▼'}
                          </button>
                          <button
                            className="btn-icon"
                            style={{ fontSize: 13, color: '#ea4335' }}
                            title="Delete"
                            onClick={() => openDel([st.id])}
                          >
                            🗑
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* EXPANDED ROW DETAIL */}
                    {expandedRow === st.id && (
                      <tr
                        key={`${st.id}-exp`}
                        style={{ background: '#f8fbff' }}
                      >
                        <td
                          colSpan={
                            COLUMNS.filter((c) => visibleCols.has(c.key))
                              .length + 2
                          }
                          style={{ padding: '14px 24px' }}
                        >
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns:
                                'repeat(auto-fit,minmax(180px,1fr))',
                              gap: 12,
                              fontSize: 13,
                            }}
                          >
                            {(
                              [
                                ['📧 Email', st.email],
                                ['📞 Phone', st.phone],
                                ['🎂 DOB', st.dob],
                                ['🏠 Address', st.address],
                                ['⚧ Gender', st.gender],
                                ['📝 Notes', st.notes || '—'],
                              ] as [string, string][]
                            ).map(([k, v]) => (
                              <div
                                key={k}
                                style={{
                                  background: '#fff',
                                  borderRadius: 8,
                                  padding: '10px 14px',
                                  border: '1px solid #e8eaed',
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: '#5f6368',
                                    marginBottom: 3,
                                    fontWeight: 600,
                                  }}
                                >
                                  {k}
                                </div>
                                <div
                                  style={{
                                    fontWeight: 500,
                                    wordBreak: 'break-all',
                                  }}
                                >
                                  {v}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}

                {pageData.length === 0 && (
                  <tr>
                    <td
                      colSpan={
                        COLUMNS.filter((c) => visibleCols.has(c.key)).length + 2
                      }
                      style={{
                        textAlign: 'center',
                        padding: 40,
                        color: '#9aa0a6',
                        fontSize: 14,
                      }}
                    >
                      😕 No students found. Try adjusting your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 20px',
              borderTop: '1px solid #f0f2f5',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <div style={{ fontSize: 13, color: '#5f6368' }}>
              Showing {Math.min((page - 1) * pageSize + 1, filtered.length)}–
              {Math.min(page * pageSize, filtered.length)} of {filtered.length}{' '}
              students
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select
                value={pageSize}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                style={{
                  padding: '5px 10px',
                  border: '1.5px solid #e0e3e8',
                  borderRadius: 7,
                  fontSize: 12,
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
              >
                {[5, 10, 20, 50].map((n) => (
                  <option key={n}>{n}</option>
                ))}
              </select>
              <button
                className="btn-icon"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                style={{ opacity: page === 1 ? 0.4 : 1 }}
              >
                ◀
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const pg = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                if (pg < 1 || pg > totalPages) return null;
                return (
                  <button
                    key={pg}
                    className="btn-icon"
                    onClick={() => setPage(pg)}
                    style={{
                      background: page === pg ? '#1a56db' : '',
                      color: page === pg ? '#fff' : '',
                      fontWeight: 600,
                      minWidth: 32,
                    }}
                  >
                    {pg}
                  </button>
                );
              })}
              <button
                className="btn-icon"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                style={{ opacity: page === totalPages ? 0.4 : 1 }}
              >
                ▶
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── DIALOG ── */}
      {dialog && (
        <div
          style={S.over}
          onClick={(e: MouseEvent<HTMLDivElement>) => {
            if (e.target === e.currentTarget) setDialog(null);
          }}
        >
          <div style={S.modal}>
            {/* DELETE CONFIRM */}
            {dialog.mode === 'delete' && (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🗑️</div>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                  Confirm Delete
                </div>
                <div
                  style={{ color: '#5f6368', fontSize: 14, marginBottom: 24 }}
                >
                  Are you sure you want to delete <b>{dialog.ids?.length}</b>{' '}
                  student(s)? This cannot be undone.
                </div>
                <div
                  style={{ display: 'flex', gap: 12, justifyContent: 'center' }}
                >
                  <button
                    style={{
                      ...S.btn,
                      background: '#f1f3f4',
                      color: '#1c1c1c',
                    }}
                    onClick={() => setDialog(null)}
                  >
                    Cancel
                  </button>
                  <button
                    style={{ ...S.btn, background: '#ea4335', color: '#fff' }}
                    onClick={() => deleteStudents(dialog.ids ?? [])}
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}

            {/* ADD / EDIT / VIEW */}
            {(dialog.mode === 'add' ||
              dialog.mode === 'edit' ||
              dialog.mode === 'view') && (
              <div>
                <div
                  style={{
                    padding: '20px 24px 16px',
                    borderBottom: '1px solid #f0f2f5',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>
                      {dialog.mode === 'add'
                        ? '➕ Add New Student'
                        : dialog.mode === 'edit'
                          ? '✏️ Edit Student'
                          : '👁 Student Details'}
                    </div>
                    {dialog.data?.roll && (
                      <div
                        style={{ fontSize: 12, color: '#5f6368', marginTop: 2 }}
                      >
                        {dialog.data.roll}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setDialog(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: 20,
                      cursor: 'pointer',
                      color: '#5f6368',
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div style={{ padding: '20px 24px' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '0 16px',
                    }}
                  >
                    <Field
                      label="Full Name"
                      name="name"
                      type="text"
                      dialogData={dialog.data}
                      readOnly={readOnly}
                      formRef={formRef}
                      def={dialog.data?.name ?? ''}
                    />
                    <Field
                      label="Email"
                      name="email"
                      type="email"
                      dialogData={dialog.data}
                      readOnly={readOnly}
                      formRef={formRef}
                      def={dialog.data?.email ?? ''}
                    />
                    <Field
                      label="Phone"
                      name="phone"
                      type="tel"
                      dialogData={dialog.data}
                      readOnly={readOnly}
                      formRef={formRef}
                      def={dialog.data?.phone ?? ''}
                    />
                    <Field
                      label="Date of Birth"
                      name="dob"
                      type="date"
                      dialogData={dialog.data}
                      readOnly={readOnly}
                      formRef={formRef}
                      def={dialog.data?.dob ?? ''}
                    />
                    <Field
                      label="Department"
                      name="department"
                      type="select"
                      dialogData={dialog.data}
                      readOnly={readOnly}
                      formRef={formRef}
                      opts={DEPARTMENTS}
                      def={dialog.data?.department ?? DEPARTMENTS[0]}
                    />
                    <Field
                      label="Year"
                      name="year"
                      type="select"
                      dialogData={dialog.data}
                      readOnly={readOnly}
                      formRef={formRef}
                      opts={YEARS}
                      def={dialog.data?.year ?? YEARS[0]}
                    />
                    <Field
                      label="Status"
                      name="status"
                      type="select"
                      dialogData={dialog.data}
                      readOnly={readOnly}
                      formRef={formRef}
                      opts={STATUSES}
                      def={dialog.data?.status ?? 'Active'}
                    />
                    <Field
                      label="Gender"
                      name="gender"
                      type="select"
                      dialogData={dialog.data}
                      readOnly={readOnly}
                      formRef={formRef}
                      opts={GENDERS}
                      def={dialog.data?.gender ?? 'Male'}
                    />
                    <Field
                      label="Grade"
                      name="grade"
                      type="select"
                      dialogData={dialog.data}
                      readOnly={readOnly}
                      formRef={formRef}
                      opts={GRADES}
                      def={dialog.data?.grade ?? 'A'}
                    />
                    <Field
                      label="GPA (0–10)"
                      name="gpa"
                      type="number"
                      dialogData={dialog.data}
                      readOnly={readOnly}
                      formRef={formRef}
                      def={dialog.data?.gpa ?? '8.00'}
                    />
                    <Field
                      label="Attendance %"
                      name="attendance"
                      type="number"
                      dialogData={dialog.data}
                      readOnly={readOnly}
                      formRef={formRef}
                      def={dialog.data?.attendance ?? 85}
                    />
                    <Field
                      label="Address"
                      name="address"
                      type="text"
                      dialogData={dialog.data}
                      readOnly={readOnly}
                      formRef={formRef}
                      def={dialog.data?.address ?? ''}
                    />
                  </div>
                  <Field
                    label="Notes / Remarks"
                    name="notes"
                    type="textarea"
                    dialogData={dialog.data}
                    readOnly={readOnly}
                    formRef={formRef}
                    def={dialog.data?.notes ?? ''}
                  />

                  {!readOnly && (
                    <div
                      style={{
                        display: 'flex',
                        gap: 10,
                        justifyContent: 'flex-end',
                        marginTop: 8,
                      }}
                    >
                      <button
                        style={{
                          ...S.btn,
                          background: '#f1f3f4',
                          color: '#1c1c1c',
                        }}
                        onClick={() => setDialog(null)}
                      >
                        Cancel
                      </button>
                      <button
                        style={{
                          ...S.btn,
                          background: '#1a56db',
                          color: '#fff',
                        }}
                        onClick={saveStudent}
                      >
                        {dialog.mode === 'add' ? 'Add Student' : 'Save Changes'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Toast toasts={toasts} />
    </div>
  );
}
